<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="bg-white shadow rounded-lg mb-6">
        <div class="px-6 py-4 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <div>
              <h1 class="text-2xl font-bold text-gray-900">{{ legalDocument?.title }}</h1>
              <p class="text-sm text-gray-600 mt-1">
                Versión {{ legalDocument?.version }} • 
                {{ legalDocument?.createdAt ? formatDate(legalDocument.createdAt) : 'N/A' }}
              </p>
            </div>
            <div class="flex space-x-3">
              <button
                @click="printDocument"
                class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg class="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                Imprimir
              </button>
              <button
                @click="downloadDocument"
                class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg class="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Descargar
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="bg-white shadow rounded-lg">
        <div class="px-6 py-8">
          <div v-if="loading" class="flex justify-center items-center py-12">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
          
          <div v-else-if="error" class="text-center py-12">
            <div class="text-red-600 mb-4">
              <svg class="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 class="text-lg font-medium text-gray-900 mb-2">Error al cargar el documento</h3>
            <p class="text-gray-600">{{ error }}</p>
            <button
              @click="loadDocument"
              class="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              Reintentar
            </button>
          </div>

          <div v-else-if="!legalDocument" class="text-center py-12">
            <div class="text-gray-400 mb-4">
              <svg class="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 class="text-lg font-medium text-gray-900 mb-2">Documento no encontrado</h3>
            <p class="text-gray-600">El documento solicitado no existe o no está disponible.</p>
          </div>

          <div v-else class="prose prose-lg max-w-none">
            <div class="text-gray-700 leading-relaxed" v-html="sanitizedDocumentContent"></div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="mt-8 text-center text-sm text-gray-500">
        <p>Este documento fue generado automáticamente el {{ new Date().toLocaleDateString('es-ES') }}</p>
        <p class="mt-1">© {{ new Date().getFullYear() }} Mascotijo. Todos los derechos reservados.</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import legalService, { type PrivacyPolicy, type TermsAndConditions } from '../services/legalService';

const route = useRoute();

const loading = ref(true);
const error = ref<string | null>(null);
const legalDocument = ref<PrivacyPolicy | TermsAndConditions | null>(null);

const sanitizeHtml = (value: string) => {
  return value
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '')
    .replace(/\son\w+\s*=\s*(['"]).*?\1/gi, '')
    .replace(/\son\w+\s*=\s*[^\s>]+/gi, '')
    .replace(/javascript\s*:/gi, '')
    .replace(/data\s*:\s*text\/html/gi, '')
}

const sanitizedDocumentContent = computed(() => sanitizeHtml(legalDocument.value?.content || ''))

// No need for formattedContent since content is already in HTML format

const loadDocument = async () => {
  try {
    loading.value = true;
    error.value = null;
    
    const type = route.params.type as string;
    
    if (type === 'privacy-policy') {
      legalDocument.value = await legalService.getActivePrivacyPolicy();
    } else if (type === 'terms-and-conditions') {
      legalDocument.value = await legalService.getActiveTermsAndConditions();
    } else {
      throw new Error('Tipo de documento no válido');
    }
    
    if (!legalDocument.value) {
      error.value = 'No hay un documento activo de este tipo';
    }
  } catch (err: any) {
    console.error('Error loading document:', err);
    error.value = err.message || 'Error al cargar el documento';
  } finally {
    loading.value = false;
  }
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const printDocument = () => {
  window.print();
};

const downloadDocument = () => {
  if (!legalDocument.value) return;
  
  const content = `${legalDocument.value.title}\n\nVersión: ${legalDocument.value.version}\nFecha: ${formatDate(legalDocument.value.createdAt)}\n\n${legalDocument.value.content}`;
  
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = `${legalDocument.value.title.replace(/\s+/g, '_')}_v${legalDocument.value.version}.txt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
};

onMounted(() => {
  loadDocument();
});
</script>

<style scoped>
@media print {
  .no-print {
    display: none !important;
  }
  
  body {
    background: white !important;
  }
  
  .shadow {
    box-shadow: none !important;
  }
}
</style>